import React, { useState, useEffect } from "react";
import { FaTrash } from "react-icons/fa";
import { Button, Form, Row, Col, Card, Alert, Spinner } from "react-bootstrap";

const API_URL = process.env.REACT_APP_API_URL || 'https://apiprov.conduto.com.ec';

const ReferenciasComercialesTable = ({ idFormulario, isReadOnly = false, onTotalChange }) => {
    const [referencias, setReferencias] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [formData, setFormData] = useState({
        nombre: "",
        telefono: "",
        contacto: ""
    });
    const [formErrors, setFormErrors] = useState({
        nombre: "",
        telefono: "",
        contacto: ""
    });

    useEffect(() => {
        if (idFormulario) {
            cargarReferencias();
        }
    }, [idFormulario]);

    const cargarReferencias = async () => {
        setLoading(true);
        setError("");
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_URL}/api/providers/referencias/${idFormulario}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: "include",
                }
            );
            if (response.ok) {
                const data = await response.json();
                setReferencias(data);
                if (onTotalChange) onTotalChange(data.length);
            } else {
                setError("Error al cargar referencias comerciales");
            }
        } catch (error) {
            setError("Error de conexión al cargar referencias");
            console.error("Error cargando referencias:", error);
        } finally {
            setLoading(false);
        }
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'nombre':
                if (!value) return 'El nombre es requerido';
                if (value.length > 255) return 'El nombre no debe superar 255 caracteres';
                return '';
            case 'telefono':
                if (!value) return 'El teléfono es requerido';
                if (!/^[0-9]{8,15}$/.test(value)) return 'Solo números, entre 8 y 15 dígitos';
                return '';
            case 'contacto':
                if (!value) return 'El contacto es requerido';
                if (value.length > 255) return 'El contacto no debe superar 255 caracteres';
                return '';
            default:
                return '';
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const validarFormulario = () => {
        const errores = {
            nombre: validateField('nombre', formData.nombre),
            telefono: validateField('telefono', formData.telefono),
            contacto: validateField('contacto', formData.contacto)
        };
        setFormErrors(errores);
        return !errores.nombre && !errores.telefono && !errores.contacto;
    };

    const agregarReferencia = async () => {
        setError("");
        if (!validarFormulario()) return;

        if (referencias.length >= 2) {
            setError("Solo puede agregar máximo 2 referencias comerciales");
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_URL}/api/providers/referencias/${idFormulario}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    credentials: "include",
                    body: JSON.stringify(formData),
                }
            );
            if (response.ok) {
                const data = await response.json();
                const nuevasReferencias = [...referencias, data.referencia];
                setReferencias(nuevasReferencias);
                setFormData({ nombre: "", telefono: "", contacto: "" });
                setFormErrors({ nombre: "", telefono: "", contacto: "" });
                if (onTotalChange) onTotalChange(nuevasReferencias.length);
            } else {
                const errorData = await response.json();
                setError(errorData.detail || "Error al guardar referencia");
            }
        } catch (error) {
            setError("Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    const eliminarReferencia = async (id) => {
        setError("");
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_URL}/api/providers/referencias/${id}`,
                {
                    method: "DELETE",
                    headers: { Authorization: `Bearer ${token}` },
                    credentials: "include",
                }
            );
            if (response.ok) {
                const nuevasReferencias = referencias.filter(ref => ref.id !== id);
                setReferencias(nuevasReferencias);
                if (onTotalChange) onTotalChange(nuevasReferencias.length);
            } else {
                setError("Error al eliminar referencia");
            }
        } catch (error) {
            setError("Error de conexión");
        }
    };

    const isAddButtonDisabled = () => {
        if (!formData.nombre.trim() || !formData.telefono.trim() || !formData.contacto.trim()) {
            return true;
        }
        if (formErrors.nombre || formErrors.telefono || formErrors.contacto) {
            return true;
        }
        if (referencias.length >= 2) {
            return true;
        }
        return false;
    };

    const total = referencias.length;
    const isValid = total >= 1;
    const hasExceeded = total > 2;

    if (loading && referencias.length === 0) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" variant="primary" size="sm" />
                <p className="mt-2 small">Cargando referencias comerciales...</p>
            </div>
        );
    }

    return (
        <div>
            <Form.Label className="required-field fw-semibold">REFERENCIAS COMERCIALES</Form.Label>
            <div className="mb-2">
                <span className="text-muted small">
                    {referencias.length} de 2 referencias máximas
                    {referencias.length === 0 && " (Requerido al menos 1)"}
                </span>
            </div>

            <div className="mb-3">
                {referencias.length === 0 ? (
                    <div className="text-center text-muted py-3 small border rounded bg-light">
                        No hay referencias comerciales agregadas.
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        <div className="row fw-semibold text-muted small px-2 d-none d-sm-flex">
                            <div className="col-sm-6">Empresa</div>
                            <div className="col-sm-2">Teléfono</div>
                            <div className="col-sm-3">Persona de contacto</div>
                            {!isReadOnly && (
                                <div className="col-sm-1 text-end">Acción</div>
                            )}
                        </div>

                        {referencias.map((ref) => (
                            <div
                                key={ref.id}
                                className="border rounded bg-white shadow-sm p-2"
                            >
                                <div className="row align-items-center g-2">
                                    <div className="col-12 col-sm-6">
                                        <small className="text-muted d-block d-sm-none">Empresa</small>
                                        <div
                                            className="small"
                                            style={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {ref.nombre}
                                        </div>
                                    </div>

                                    <div className="col-12 col-sm-2">
                                        <small className="text-muted d-block d-sm-none">Teléfono</small>
                                        <div className="small">
                                            {ref.telefono}
                                        </div>
                                    </div>

                                    <div className="col-12 col-sm-3">
                                        <small className="text-muted d-block d-sm-none">Contacto</small>
                                        <div
                                            className="small"
                                            style={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {ref.contacto}
                                        </div>
                                    </div>

                                    {!isReadOnly && (
                                        <div className="col-6 col-sm-1 d-flex justify-content-end">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => eliminarReferencia(ref.id)}
                                                title="Eliminar"
                                                className="p-1 d-flex align-items-center justify-content-center"
                                                style={{ width: "32px", height: "32px" }}
                                            >
                                                <FaTrash size={12} />
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {!isReadOnly && (
                <Card className="bg-light border-0 mb-3">
                    <Card.Body className="p-2 p-sm-3">
                        <Row className="g-2">
                            <Col xs={12} sm={6} md={5}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold mb-1">Empresa</Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        isInvalid={!!formErrors.nombre}
                                        name="nombre"
                                        placeholder="Nombre de la empresa"
                                        value={formData.nombre}
                                        onChange={handleInputChange}
                                        maxLength="255"
                                        style={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    />
                                    {formErrors.nombre && (
                                        <Form.Text className="text-danger small">{formErrors.nombre}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold mb-1">Teléfono</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        size="sm"
                                        isInvalid={!!formErrors.telefono}
                                        name="telefono"
                                        placeholder="Teléfono"
                                        value={formData.telefono}
                                        onChange={handleInputChange}
                                        maxLength="15"
                                    />
                                    {formErrors.telefono && (
                                        <Form.Text className="text-danger small">{formErrors.telefono}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col xs={12} sm={6} md={3}>
                                <Form.Group>
                                    <Form.Label className="small fw-semibold mb-1">Persona de contacto</Form.Label>
                                    <Form.Control
                                        type="text"
                                        size="sm"
                                        isInvalid={!!formErrors.contacto}
                                        name="contacto"
                                        placeholder="Persona de contacto"
                                        value={formData.contacto}
                                        onChange={handleInputChange}
                                        maxLength="255"
                                        style={{
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    />
                                    {formErrors.contacto && (
                                        <Form.Text className="text-danger small">{formErrors.contacto}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col xs={6} sm={2} md={1} className="d-flex align-items-end">
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="w-100"
                                    onClick={agregarReferencia}
                                    disabled={isAddButtonDisabled()}
                                >
                                    <i className="bi bi-plus-circle me-1"></i>Agregar
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {error && (
                <Alert variant="danger" className="mt-3 py-2 small" dismissible onClose={() => setError("")}>
                    {error}
                </Alert>
            )}
        </div>
    );
};

export default ReferenciasComercialesTable;